import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, latestComics } from '@/mocks/comics';

const categoryNames: Record<string, string> = {
  romance: '恋爱',
  fantasy: '奇幻',
  action: '动作',
  suspense: '悬疑',
  comedy: '搞笑',
  drama: '剧情',
  school: '校园',
  horror: '惊悚',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const categoryName = categoryNames[slug || ''] || '分类';
  const categoryData = categories.find((c) => c.slug === slug);
  const filteredComics = latestComics.filter(
    (c) => c.category === categoryName
  );

  const displayComics = filteredComics.length > 0 ? filteredComics : latestComics.slice(0, 8);

  const jsonLdBreadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'http://ipv6c.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: `${categoryName}漫画`,
        item: `http://ipv6c.com/category/${slug}`,
      },
    ],
  };

  const jsonLdCollectionPage = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName}漫画 - 韩漫画网`,
    description: `韩漫画网${categoryName}漫画分类，海量正版${categoryName}漫画免费在线阅读。共${categoryData?.count || 0}部作品，每日更新。`,
    url: `http://ipv6c.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫画网',
      url: 'http://ipv6c.com/',
    },
  };

  const bgColors = [
    'bg-primary-100/40',
    'bg-accent-100/40',
    'bg-secondary-100/40',
    'bg-background-200/60',
    'bg-primary-100/30',
    'bg-accent-100/30',
    'bg-secondary-100/30',
    'bg-background-200/50',
  ];

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdBreadcrumb) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdCollectionPage) }}
      />
      <Navbar />

      <main className="pt-16">
        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">
                首页
              </Link>
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </span>
              <span className="text-foreground-800 font-medium">{categoryName}漫画</span>
            </nav>

            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-2">
              {categoryName}漫画
            </h1>
            {categoryData && (
              <p className="text-sm text-foreground-600 mb-8">
                共 {categoryData.count} 部作品
              </p>
            )}

            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide flex-wrap">
              <Link
                to="/category/romance"
                className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap cursor-pointer transition-all duration-200 ${
                  slug === 'romance'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:text-foreground-900 hover:bg-background-200 border border-background-200/70'
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-heart-line text-xs"></i>
                </span>
                恋爱
              </Link>
              <Link
                to="/category/fantasy"
                className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap cursor-pointer transition-all duration-200 ${
                  slug === 'fantasy'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:text-foreground-900 hover:bg-background-200 border border-background-200/70'
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-magic-line text-xs"></i>
                </span>
                奇幻
              </Link>
              <Link
                to="/category/action"
                className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap cursor-pointer transition-all duration-200 ${
                  slug === 'action'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:text-foreground-900 hover:bg-background-200 border border-background-200/70'
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-flashlight-line text-xs"></i>
                </span>
                动作
              </Link>
              <Link
                to="/category/suspense"
                className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap cursor-pointer transition-all duration-200 ${
                  slug === 'suspense'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:text-foreground-900 hover:bg-background-200 border border-background-200/70'
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-search-eye-line text-xs"></i>
                </span>
                悬疑
              </Link>
              <Link
                to="/category/comedy"
                className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap cursor-pointer transition-all duration-200 ${
                  slug === 'comedy'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:text-foreground-900 hover:bg-background-200 border border-background-200/70'
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-emotion-laugh-line text-xs"></i>
                </span>
                搞笑
              </Link>
              <Link
                to="/category/drama"
                className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap cursor-pointer transition-all duration-200 ${
                  slug === 'drama'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:text-foreground-900 hover:bg-background-200 border border-background-200/70'
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-film-line text-xs"></i>
                </span>
                剧情
              </Link>
              <Link
                to="/category/school"
                className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap cursor-pointer transition-all duration-200 ${
                  slug === 'school'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:text-foreground-900 hover:bg-background-200 border border-background-200/70'
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-building-line text-xs"></i>
                </span>
                校园
              </Link>
              <Link
                to="/category/horror"
                className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap cursor-pointer transition-all duration-200 ${
                  slug === 'horror'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:text-foreground-900 hover:bg-background-200 border border-background-200/70'
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-ghost-line text-xs"></i>
                </span>
                惊悚
              </Link>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center gap-2 mb-8">
              <span className="w-1.5 h-1.5 rounded-full bg-foreground-900"></span>
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900">
                热门{categoryName}漫画
              </h2>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {displayComics.map((comic, index) => (
                <Link
                  key={comic.id}
                  to={`/article/${comic.id}`}
                  className="group rounded-2xl overflow-hidden cursor-pointer block transition-transform duration-300 hover:-translate-y-1"
                >
                  <div className={`${bgColors[index % bgColors.length]} p-3 md:p-4`}>
                    <div className="relative overflow-hidden rounded-xl aspect-[3/4]">
                      <img
                        src={comic.cover}
                        alt={comic.title}
                        loading="lazy"
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                  </div>
                  <div className="px-3 md:px-4 pb-3 md:pb-4 pt-2 bg-background-50">
                    <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-900 line-clamp-1 mb-1">
                      {comic.title}
                    </h3>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-foreground-500">{comic.author}</span>
                      <span className="text-xs text-accent-600 font-medium">{comic.latestChapter}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {displayComics.length === 0 && (
              <div className="text-center py-20">
                <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-foreground-300">
                  <i className="ri-book-2-line text-4xl"></i>
                </span>
                <p className="text-foreground-500">暂无该分类漫画，请浏览其他分类</p>
              </div>
            )}
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10 items-center">
              <div className="lg:w-3/5">
                <span className="text-xs text-accent-600 font-medium">读者口碑</span>
                <blockquote className="mt-2 text-xl md:text-3xl font-heading font-semibold text-foreground-900 leading-relaxed">
                  &ldquo;韩漫画网的{categoryName}漫画资源真的太丰富了，每天都有新内容更新，画质清晰，阅读体验超棒！&rdquo;
                </blockquote>
                <div className="flex items-center gap-2 mt-4">
                  <span className="text-sm font-medium text-foreground-800">— 漫画爱好者</span>
                  <span className="w-4 h-4 flex items-center justify-center text-accent-500">
                    <i className="ri-verified-badge-fill text-sm"></i>
                  </span>
                </div>
              </div>
              <div className="lg:w-2/5">
                <img
                  src="https://readdy.ai/api/search-image?query=Soft%20black%20and%20white%20portrait%20photography%2C%20young%20Asian%20person%20reading%20on%20tablet%2C%20side%20profile%2C%20looking%20up%20thoughtfully%2C%20clean%20white%20background%2C%20natural%20window%20light%2C%20editorial%20style%2C%20minimal%20composition%2C%20warm%20grayscale%20tones%2C%20artistic%20portrait&width=600&height=600&seq=testimonial-reader-portrait&orientation=squarish"
                  alt="漫画读者评价"
                  className="w-full max-w-xs mx-auto rounded-2xl"
                />
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}